/**
 * Help Service
 *
 * Handles API calls for the public Help page and the super-admin Help Videos management.
 *
 * Public endpoint:
 *   GET /api/help/sections — returns published sections with published videos
 *
 * Admin endpoints (all under /api/admin/help):
 *   GET    /sections            — all sections incl. unpublished, with videos
 *   POST   /sections            — create section
 *   PATCH  /sections/:id        — update section (partial)
 *   DELETE /sections/:id        — delete section (cascade deletes videos)
 *   POST   /sections/reorder    — reorder sections { orderedIds }
 *   POST   /videos              — create video (backend auto-parses URL)
 *   PATCH  /videos/:id          — update video (partial)
 *   DELETE /videos/:id          — delete video
 *   POST   /sections/:id/videos/reorder — reorder videos within section
 */

import apiClient from '@/services/api';
import type {
  HelpSection,
  HelpVideo,
  CreateSectionInput,
  UpdateSectionInput,
  CreateVideoInput,
  UpdateVideoInput,
} from '@/types/help';

// ---- Public ----

export const getHelpContent = async (): Promise<HelpSection[]> => {
  const response = await apiClient.get<{ success: boolean; data: HelpSection[] }>(
    '/api/help/sections'
  );
  return response.data.data;
};

// ---- Admin ----

export const helpAdminService = {
  listAllSections: async (): Promise<HelpSection[]> => {
    const response = await apiClient.get<{ success: boolean; data: HelpSection[] }>(
      '/api/admin/help/sections'
    );
    return response.data.data;
  },

  createSection: async (input: CreateSectionInput): Promise<HelpSection> => {
    const response = await apiClient.post<{ success: boolean; data: HelpSection }>(
      '/api/admin/help/sections',
      input
    );
    return response.data.data;
  },

  updateSection: async (id: number, input: UpdateSectionInput): Promise<HelpSection> => {
    const response = await apiClient.patch<{ success: boolean; data: HelpSection }>(
      `/api/admin/help/sections/${id}`,
      input
    );
    return response.data.data;
  },

  deleteSection: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/admin/help/sections/${id}`);
  },

  reorderSections: async (orderedIds: number[]): Promise<void> => {
    await apiClient.post('/api/admin/help/sections/reorder', { orderedIds });
  },

  createVideo: async (input: CreateVideoInput): Promise<HelpVideo> => {
    const response = await apiClient.post<{ success: boolean; data: HelpVideo }>(
      '/api/admin/help/videos',
      input
    );
    return response.data.data;
  },

  updateVideo: async (id: number, input: UpdateVideoInput): Promise<HelpVideo> => {
    const response = await apiClient.patch<{ success: boolean; data: HelpVideo }>(
      `/api/admin/help/videos/${id}`,
      input
    );
    return response.data.data;
  },

  deleteVideo: async (id: number): Promise<void> => {
    await apiClient.delete(`/api/admin/help/videos/${id}`);
  },

  reorderVideos: async (sectionId: number, orderedIds: number[]): Promise<void> => {
    await apiClient.post(`/api/admin/help/sections/${sectionId}/videos/reorder`, { orderedIds });
  },
};
